import { Formik } from "formik";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { newGetInitialValues, newValidate, Translation as T, useAmsSelector, useTranslation } from "@awe-web/shared/lib";
import {
  Button,
  FormikErrorMessage,
  FormikLabeledCheckbox,
  FormikLabeledField,
  FormikTextareaWithCharacterCount,
  RequiredNotice,
  ScrollToError
} from "../../../components/form.js";
import { Col, Row } from "../../../components/grid.js";
import { TypeArea } from "../../../components/layout.js";
import {
  ScreenHeading,
  ScreenLoading,
  ScreenSection,
  ScreenSectionHeading,
  ScreenSectionSubheading,
  ScreenSubsection
} from "../../../components/screen.js";
import TranslationWithCmsLink from "../../../l10n/components/translation_with_cms_link.jsx";
import { LtrMonthlyTotalPrice, LtrOneTimeTotalPrice } from "../../../rent_contracts/components/price";
import { DriverDetails, PaymentDetails, ProvisioningDetails, ReturnDetails } from "../../../rent_contracts/components/rent_contract";
import SelectedRentObject from "../../../rent_objects/components/selected_rent_object.jsx";
import SelectedPeriod from "../../../stations/components/selected_period.jsx";
import { useInitialize } from "../../../util/react.js";

import { B2B_LTR_SelectDriverAndProvisioning } from "../../../routing/routes.js";
import { initialize, submit } from "./actions.js";

import "./submit_rent_contract.scss";

const OVERVIEW = "FSD0000159";
const SELECTED_RENT_OBJECT = "ALG0000091";
const SELECTED_TIME = "ALG0000092";
const CHANGE_VALUES = "ALG0000025";
const DRIVER_DETAILS = "ALG0000094";
const PAYMENT_DETAILS = "ALG0000093";
const PROVISIONING_DETAILS = "ALG0000095";
const RETURN_DETAILS = "FSD0000161";
const RETURN_NOTICE = "ALG0000246";
const TEXTBOX_LABEL = "ALG0000104";
const TEXTBOX_PLACEHOLDER = "ALG0000105";
const COSTS = "FSD0000163";
const I_ACCEPT_THE_TOS = "FSD0000286";
const I_ACCEPT_THE_PRIVACY_POLICY = "FSD0000287";
const SUBMIT_RESERVATION = "FSD0000174";
const NOTICE = "FSD0000209";
const PRICES_NOTICE = "ALG0000029";
const ADDITIONAL_INFORMATION = "ALG0000110";
const EXCLUDING_VAT = "ALG0000245";

function SubmitRentContractForm({handleSubmit}) {
  const {translate} = useTranslation();

  return (
    <form onSubmit={handleSubmit}>
      <ScrollToError />

      <ScreenSection main>
        <TypeArea className="b2b-ltr-submit-rent-contract__type-area">
          <Row>
            <Col tablet={{cols: 4}} desktop={{cols: 4, offset: 2}}>
              <ScreenSubsection className="b2c-str-submit-rent-contract__type-area__mobile">
                <ScreenSectionHeading light>
                  <T code={DRIVER_DETAILS} />
                </ScreenSectionHeading>
                <DriverDetails />
              </ScreenSubsection>

              <ScreenSubsection className="b2c-str-submit-rent-contract__type-area__mobile">
                <ScreenSectionHeading light>
                  <T code={PROVISIONING_DETAILS} />
                </ScreenSectionHeading>
                <ProvisioningDetails reservationPath="B2B" />
              </ScreenSubsection>

              <ScreenSubsection className="b2c-str-submit-rent-contract__type-area__mobile">
                <ScreenSectionHeading light>
                  <T code={RETURN_DETAILS} />
                </ScreenSectionHeading>
                <ReturnDetails />
                <div className="b2b-ltr-submit-rent-contract__return-notice">
                  <T code={RETURN_NOTICE} />
                </div>
              </ScreenSubsection>

              <ScreenSubsection className="b2c-str-submit-rent-contract__type-area__mobile">
                <FormikLabeledField
                  name="contractNote"
                  as={FormikTextareaWithCharacterCount}
                  getFieldLabel={() => translate(TEXTBOX_LABEL)}
                  maxLength={500}
                  placeholder={translate(TEXTBOX_PLACEHOLDER)}
                />
              </ScreenSubsection>
            </Col>

            <Col tablet={{cols: 4}} desktop={{cols: 4}}>
              <ScreenSubsection className="b2c-str-submit-rent-contract__type-area__mobile">
                <ScreenSectionHeading light>
                  <T code={PAYMENT_DETAILS} />
                </ScreenSectionHeading>
                <PaymentDetails />
              </ScreenSubsection>

              <ScreenSubsection>
                <ScreenSectionHeading light className="b2c-str-submit-rent-contract__type-area__mobile">
                  <T code={COSTS} />
                </ScreenSectionHeading>

                <LtrMonthlyTotalPrice descriptionHint={translate(EXCLUDING_VAT)} />

                <LtrOneTimeTotalPrice
                  className="b2b-ltr-submit-rent-contract__one-time-total-price"
                  descriptionHint={translate(EXCLUDING_VAT)}
                />
              </ScreenSubsection>

              <ScreenSubsection className="b2c-str-submit-rent-contract__type-area__mobile">
                <div className="b2b-ltr-submit-rent-contract__checkbox-tos">
                  <FormikLabeledCheckbox name="agbAccepted">
                    <TranslationWithCmsLink
                      className="b2b-ltr-submit-rent-contract__required-checkbox-label checkbox-label"
                      code={I_ACCEPT_THE_TOS}
                      pageName="agb"
                    />
                  </FormikLabeledCheckbox>
                  <FormikErrorMessage name="agbAccepted" />
                </div>
                <div className="b2b-ltr-submit-rent-contract__checkbox-privacy">
                  <FormikLabeledCheckbox name="privacyAccepted">
                    <TranslationWithCmsLink
                      className="b2b-ltr-submit-rent-contract__required-checkbox-label checkbox-label"
                      code={I_ACCEPT_THE_PRIVACY_POLICY}
                      pageName="privacy"
                    />
                  </FormikLabeledCheckbox>
                  <FormikErrorMessage name="privacyAccepted" />
                </div>

                <Button primary continue submit className="b2b-ltr-submit-rent-contract__submit">
                  <T code={SUBMIT_RESERVATION} />
                </Button>
              </ScreenSubsection>
            </Col>
          </Row>
          <Row>
            <Col desktop={{cols: 8, offset: 2}} className="b2c-str-submit-rent-contract__type-area__mobile">
              <RequiredNotice className="b2b-ltr-submit-rent-contract__required-notice">
                <T code={NOTICE} />
              </RequiredNotice>
              <div className="b2b-ltr-submit-rent-contract__additional-information"><T code={ADDITIONAL_INFORMATION} /></div>
              <div><T code={PRICES_NOTICE} /></div>
            </Col>
          </Row>
        </TypeArea>
      </ScreenSection>
    </form>
  );
}

function InternalSubmitRentContractForm({onSubmit}) {
  const validate = useAmsSelector((state) => newValidate("submitB2bLtrRentContract")(state));

  const initialValues = useAmsSelector((state) => {
    const formInitializer = newGetInitialValues("submitB2bLtrRentContract");
    const inialValuesFactory = formInitializer(state);
    return inialValuesFactory({
      contractNote: "",
      agbAccepted: false,
      privacyAccepted: false
    });
  });
  
  return (
    <Formik validate={validate} initialValues={initialValues} onSubmit={onSubmit} >
      {(formikProps) => (
        <SubmitRentContractForm {...formikProps} />
      )}
    </Formik>
  );
}

export default function B2B_LTR_SubmitRentContract() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = useCallback(async (values, formikProps) => {
    const nextPath = await dispatch(submit(values, formikProps));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const init = useCallback(() => dispatch(initialize()), [dispatch]);
  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2b-ltr-submit-rent-contract">
      <ScreenSection dark>
        <TypeArea>
          <Row>
            <Col desktop={{cols: 8, offset: 2}}>
              <ScreenHeading><T code={OVERVIEW} /></ScreenHeading>
              <div className="b2b-ltr-submit-rent-contract__selected-rent-object">
                <ScreenSectionSubheading>
                  <T code={SELECTED_RENT_OBJECT} />
                </ScreenSectionSubheading>
                <SelectedRentObject selectedBasicProperties="B2B" />
              </div>

              <div>
                <ScreenSectionSubheading>
                  <T code={SELECTED_TIME} />
                </ScreenSectionSubheading>
                <SelectedPeriod
                  className="b2b-ltr-submit-rent-contract__selected-period"
                  editButtonLocation={B2B_LTR_SelectDriverAndProvisioning.path()}
                />
                <Button className="b2b-str-submit-rent-contract__mobile-back-button" secondary back to={B2B_LTR_SelectDriverAndProvisioning.path()} >
                  {<T code={CHANGE_VALUES} />}
                </Button>
              </div>
            </Col>
          </Row>
        </TypeArea>
      </ScreenSection>

      <InternalSubmitRentContractForm onSubmit={onSubmit} />
    </div>
  );
}
